import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, List, ListItem, ListItemIcon, ListItemText,
  ListItemButton, Breadcrumbs, Link, IconButton, Tooltip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip,
  Alert, Paper, Divider, TextField,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import ArticleIcon from '@mui/icons-material/Article';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import HomeIcon from '@mui/icons-material/Home';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import CloudIcon from '@mui/icons-material/Cloud';

import { filesAPI } from '../../services/api';

/* ─── helpers ─── */
function humanSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fileIcon(entry) {
  if (entry.type === 'dir') return <FolderIcon sx={{ color: '#f5a623' }} />;
  const mime = entry.mime || '';
  if (entry.is_google_native) return <ArticleIcon sx={{ color: '#1a73e8' }} />;
  if (mime.startsWith('image/')) return <ImageIcon color="success" />;
  if (mime === 'application/pdf') return <PictureAsPdfIcon color="error" />;
  if (mime.startsWith('text/')) return <ArticleIcon color="info" />;
  return <InsertDriveFileIcon color="action" />;
}

/** Etiqueta corta del tipo de fichero para el chip de la lista. */
function mimeLabel(entry) {
  if (entry.is_google_native) {
    const kind = (entry.mime || '').split('.').pop();
    return { document: 'Documento', spreadsheet: 'Hoja de cálculo',
      presentation: 'Presentación', drawing: 'Dibujo' }[kind] || 'Google';
  }
  if (!entry.mime) return null;
  return entry.mime.split('/')[1] || entry.mime;
}

/* ─── file preview dialog ─── */
function PreviewDialog({ entry, onClose }) {
  const open = Boolean(entry);
  const [blobUrl, setBlobUrl] = useState(null);
  const [blobType, setBlobType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const name = entry?.name || '';

  // El contenido se pide con el token del usuario y se expone como object URL,
  // que si es lo que pueden consumir <iframe>, <img> y el enlace de descarga.
  useEffect(() => {
    if (!entry) return undefined;
    let objectUrl = null;
    let cancelled = false;

    setLoading(true);
    setError(null);
    setBlobUrl(null);

    filesAPI.fetchBlob(entry.path)
      .then((resp) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(resp.data);
        setBlobUrl(objectUrl);
        setBlobType(resp.data.type || entry.mime || '');
      })
      .catch(async (err) => {
        if (cancelled) return;
        // El error llega como blob porque la peticion pide responseType blob
        let detail = 'No se ha podido cargar el fichero.';
        try {
          const text = await err.response?.data?.text?.();
          if (text) detail = JSON.parse(text).detail || detail;
        } catch { /* se queda el mensaje generico */ }
        setError(detail);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [entry]);

  const isImage = blobType.startsWith('image/');
  const isPdf = blobType === 'application/pdf';
  const isText = blobType.startsWith('text/') || blobType === 'application/json';
  const driveLink = entry?.web_view_link;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        {fileIcon(entry || {})}
        <Box sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </Box>
        {driveLink && (
          <Tooltip title="Abrir y editar en Google Drive">
            <IconButton href={driveLink} target="_blank" rel="noopener noreferrer" size="small">
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Descargar">
          <span>
            <IconButton component="a" href={blobUrl || undefined} download={name}
              size="small" disabled={!blobUrl}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ flexGrow: 1, p: 0, overflow: 'hidden' }}>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>
            {driveLink && (
              <Button variant="contained" href={driveLink} target="_blank"
                rel="noopener noreferrer" startIcon={<OpenInNewIcon />}>
                Abrir en Google Drive
              </Button>
            )}
          </Box>
        )}

        {!loading && !error && blobUrl && (
          <>
            {isPdf && (
              <iframe src={blobUrl} title={name}
                style={{ width: '100%', height: '100%', border: 'none' }} />
            )}
            {isImage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 2 }}>
                <img src={blobUrl} alt={name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </Box>
            )}
            {isText && !isPdf && !isImage && <TextPreview url={blobUrl} />}
            {!isPdf && !isImage && !isText && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                <InsertDriveFileIcon sx={{ fontSize: 80, color: 'text.disabled' }} />
                <Typography color="text.secondary">Este tipo de fichero no tiene vista previa.</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" component="a" href={blobUrl} download={name}
                    startIcon={<DownloadIcon />}>
                    Descargar
                  </Button>
                  {driveLink && (
                    <Button variant="outlined" href={driveLink} target="_blank"
                      rel="noopener noreferrer" startIcon={<OpenInNewIcon />}>
                      Abrir en Drive
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TextPreview({ url }) {
  const [text, setText] = useState(null);
  useEffect(() => {
    fetch(url).then(r => r.text()).then(setText).catch(() => setText('Error al cargar el fichero.'));
  }, [url]);
  return (
    <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      {text == null ? <CircularProgress /> : (
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: 13 }}>{text}</pre>
      )}
    </Box>
  );
}

/* ─── nueva carpeta ─── */
function NewFolderDialog({ open, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await onCreate(name.trim());
    setSaving(false);
    setName('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Nueva carpeta</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth margin="dense" label="Nombre de la carpeta"
          value={name} onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleCreate} disabled={!name.trim() || saving}>
          {saving ? 'Creando…' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ─── main component ─── */
export default function FileBrowser({ rootPath = null, title = 'Documentos' }) {
  const [data, setData] = useState(null);      // { backend, path, base_path, parent_path, breadcrumbs, entries }
  const [currentPath, setCurrentPath] = useState(rootPath);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [needsGoogle, setNeedsGoogle] = useState(false);
  const [preview, setPreview] = useState(null); // entry to preview
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const uploadInputRef = useRef(null);

  const load = useCallback(async (path) => {
    setLoading(true);
    setError(null);
    setNeedsGoogle(false);
    try {
      const r = await filesAPI.browse(path);
      setData(r.data);
      setCurrentPath(r.data.path);
    } catch (err) {
      // 428 = el almacen es Drive y el usuario aun no ha conectado su cuenta
      if (err.response?.status === 428) setNeedsGoogle(true);
      setError(err.response?.data?.detail || 'Error al leer el directorio');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // When rootPath changes (e.g. project loaded after component mount)
  useEffect(() => {
    setCurrentPath(rootPath);
    load(rootPath);
  }, [rootPath, load]);

  const handleUploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadError(null);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await filesAPI.upload(currentPath, formData);
      }
      await load(currentPath);
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Error al subir el fichero');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateFolder = async (name) => {
    try {
      await filesAPI.mkdir(currentPath, name);
      setNewFolderOpen(false);
      await load(currentPath);
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Error al crear la carpeta');
    }
  };

  const navigate = (path) => load(path);

  const goUp = () => {
    if (data?.parent_path) load(data.parent_path);
  };

  const goHome = () => load(rootPath);

  const handleEntryClick = (entry) => {
    if (entry.type === 'dir') {
      navigate(entry.path);
    } else {
      setPreview(entry);
    }
  };

  const breadcrumbs = data?.breadcrumbs || [];
  const canGoUp = Boolean(data?.parent_path);
  const rootIsBase = !rootPath || (data && data.path === data.base_path);
  const isDrive = data?.backend === 'drive';

  return (
    <Box>
      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {/* Home / go-up buttons */}
        {!rootIsBase && (
          <Tooltip title="Raíz del proyecto">
            <span>
              <IconButton size="small" onClick={goHome} disabled={loading}>
                <HomeIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Subir un nivel">
          <span>
            <IconButton size="small" onClick={goUp} disabled={!canGoUp || loading}>
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Actualizar">
          <span>
            <IconButton size="small" onClick={() => load(currentPath)} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ flexGrow: 1 }} maxItems={6}>
          <Link
            component="button"
            variant="body2"
            onClick={goHome}
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <HomeIcon sx={{ fontSize: 14 }} />
            {rootPath && !isDrive
              ? (rootPath.split('/').filter(Boolean).pop() || title)
              : title}
          </Link>
          {breadcrumbs.map((crumb, i) => {
            const isLast = i === breadcrumbs.length - 1;
            return isLast ? (
              <Typography key={crumb.path} variant="body2" color="text.primary" fontWeight="medium">
                {crumb.label}
              </Typography>
            ) : (
              <Link key={crumb.path} component="button" variant="body2"
                underline="hover" onClick={() => navigate(crumb.path)}>
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>

        {isDrive && (
          <Chip icon={<CloudIcon />} label="Drive" size="small" variant="outlined"
            color="primary" sx={{ height: 22, fontSize: 11 }} />
        )}

        <input
          ref={uploadInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleUploadFiles(Array.from(e.target.files || []))}
        />
        <Tooltip title="Nueva carpeta">
          <span>
            <IconButton size="small" onClick={() => setNewFolderOpen(true)} disabled={!currentPath || loading}>
              <CreateNewFolderIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Subir fichero(s) a la carpeta actual">
          <span>
            <IconButton
              size="small"
              onClick={() => uploadInputRef.current?.click()}
              disabled={uploading || !currentPath}
              color="primary"
            >
              {uploading ? <CircularProgress size={16} /> : <CloudUploadIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        {loading && <CircularProgress size={18} />}
      </Box>

      {/* Current path hint */}
      {data?.path && !isDrive && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1, pl: 0.5 }}>
          {data.path}
        </Typography>
      )}
      {isDrive && data?.web_view_link && (
        <Typography variant="caption" sx={{ display: 'block', mb: 1, pl: 0.5 }}>
          <Link href={data.web_view_link} target="_blank" rel="noopener noreferrer" underline="hover">
            Abrir esta carpeta en Google Drive
          </Link>
        </Typography>
      )}

      {needsGoogle && (
        <Alert severity="info" sx={{ mb: 1 }}
          action={<Button size="small" href="/profile">Conectar</Button>}>
          Los documentos están en la unidad compartida de Drive. Conecta tu cuenta de Google
          desde tu perfil para poder verlos.
        </Alert>
      )}
      {error && !needsGoogle && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {uploadError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      )}

      {/* File list with drag & drop */}
      <Paper
        variant="outlined"
        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); }}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragging(false);
          handleUploadFiles(Array.from(e.dataTransfer.files || []));
        }}
        sx={{
          position: 'relative',
          border: dragging ? '2px dashed' : '1px solid',
          borderColor: dragging ? 'primary.main' : 'divider',
          transition: 'all 0.15s',
        }}
      >
        {dragging && (
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            borderRadius: 'inherit',
            pointerEvents: 'none',
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              <Typography variant="body1" color="primary" fontWeight="medium">
                Suelta aquí para subir
              </Typography>
            </Box>
          </Box>
        )}
        {data?.entries?.length === 0 && !loading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">Carpeta vacía · arrastra ficheros aquí para subirlos</Typography>
          </Box>
        )}
        <List dense disablePadding>
          {(data?.entries || []).map((entry, idx) => (
            <React.Fragment key={entry.path}>
              {idx > 0 && <Divider component="li" />}
              <ListItem
                disablePadding
                secondaryAction={entry.web_view_link ? (
                  <Tooltip title="Abrir en Google Drive">
                    <IconButton edge="end" size="small" href={entry.web_view_link}
                      target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}>
                      <OpenInNewIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                ) : null}
              >
                <ListItemButton onClick={() => handleEntryClick(entry)} sx={{ py: 0.75 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>{fileIcon(entry)}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={entry.type === 'dir' ? 'medium' : 'regular'}>
                        {entry.name}
                      </Typography>
                    }
                    // El secundario lleva un Chip (un <div>), asi que no puede
                    // renderizarse dentro del <p> que MUI usa por defecto.
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={
                      entry.type === 'file' ? (
                        <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          {mimeLabel(entry) && (
                            <Chip label={mimeLabel(entry)}
                              size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                          )}
                          {entry.size != null && <span>{humanSize(entry.size)}</span>}
                          <span>·</span>
                          <span>{formatDate(entry.modified)}</span>
                        </Box>
                      ) : null
                    }
                  />
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {/* Preview modal */}
      <PreviewDialog entry={preview} onClose={() => setPreview(null)} />
      <NewFolderDialog open={newFolderOpen} onClose={() => setNewFolderOpen(false)}
        onCreate={handleCreateFolder} />
    </Box>
  );
}
